import React, { JSX, useEffect, useRef } from 'react';

type ProvidersLocationsMapProps = {
  searchType: string;
  coords?: Array<Array<number>>;
  iconUrl?: string;
};

type ProviderLocationPoint = {
  index: number;
  latitude: number;
  longitude: number;
  originalLatitude: number;
  originalLongitude: number;
};

const DEFAULT_CENTER: [number, number] = [-89.5889864, 40.6936488];
const PROVIDER_MARKER_IMAGE_ID = 'provider-location-marker';

const ProvidersLocationsMap = (
  props: ProvidersLocationsMapProps
): JSX.Element => {
  const { coords, iconUrl } = props;

  const mapRef = useRef<any>(null);
  const dataSourceRef = useRef<any>(null);
  const atlasRef = useRef<any>(null);
  const mapDivRef = useRef<HTMLElement | null>(null);

  const selectorFor = (lat: number, lng: number) => {
    const latStr = parseFloat(lat.toFixed(6));
    const lngStr = parseFloat(lng.toFixed(6));

    return `[data-coords="${latStr}, ${lngStr}"],[data-coords="${latStr},${lngStr}"]`;
  };

  const scrollToLocation = (lat: number, lng: number) => {
    const target = document.querySelector(selectorFor(lat, lng));

    if (target) {
      (target as HTMLElement).scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const sameCoords = (coordArrays: number[][]) => {
    if (coordArrays.length <= 1) return true;

    const [firstLat, firstLng] = coordArrays[0];

    return coordArrays
      .slice(1)
      .every(([lat, lng]) => lat === firstLat && lng === firstLng);
  };

  const getBounds = (
    atlas: any,
    points: Array<ProviderLocationPoint>
  ): any | null => {
    if (!points.length) return null;

    const positions = points.map(point => [
      point.longitude,
      point.latitude,
    ]);

    if (positions.length === 1) {
      const point = positions[0];

      return atlas.data.BoundingBox.fromPositions([
        [point[0] - 0.01, point[1] - 0.01],
        [point[0] + 0.01, point[1] + 0.01],
      ]);
    }

    return atlas.data.BoundingBox.fromPositions(positions);
  };

  const buildPoints = (coordList: Array<Array<number>>) => {
    const duplicateCoords = sameCoords(coordList);

    return coordList.map(([lat, lng], index) => {
      if (!duplicateCoords) {
        return {
          index: index + 1,
          latitude: lat,
          longitude: lng,
          originalLatitude: lat,
          originalLongitude: lng,
        };
      }

      const angle = (2 * Math.PI * index) / coordList.length;
      const offset = coordList.length === 1 ? 0 : 0.00008;

      return {
        index: index + 1,
        latitude: lat + Math.sin(angle) * offset,
        longitude: lng + Math.cos(angle) * offset,
        originalLatitude: lat,
        originalLongitude: lng,
      };
    });
  };

  const setMapFeatures = (
    atlas: any,
    dataSource: any,
    points: Array<ProviderLocationPoint>
  ) => {
    const features = points.map(point => {
      return new atlas.data.Feature(
        new atlas.data.Point([
          point.longitude,
          point.latitude,
        ]),
        {
          index: point.index,
          latitude: point.latitude,
          longitude: point.longitude,
          originalLatitude: point.originalLatitude,
          originalLongitude: point.originalLongitude,
        }
      );
    });

    dataSource.clear();

    if (features.length) {
      dataSource.add(features);
    }
  };

  useEffect(() => {
    if (!coords || !coords.length || !iconUrl) return;

    let disposed = false;

    const initializeAzureMap = async () => {
      if (typeof window === 'undefined') return;

      const mapDiv = document.getElementById('map-container');

      if (!mapDiv) return;

      mapDivRef.current = mapDiv;
      mapDiv.innerHTML = '';

      const subscriptionKey =
        process.env.NEXT_PUBLIC_AZURE_MAPS_SUBSCRIPTION_KEY;

      if (!subscriptionKey) return;

      const atlasModule = await import('azure-maps-control');
      const atlas = atlasModule.default || atlasModule;

      if (disposed) return;

      atlasRef.current = atlas;

      atlas.setSubscriptionKey(subscriptionKey);
      atlas.setAuthenticationOptions({
        authType: atlas.AuthenticationType.subscriptionKey,
        subscriptionKey,
      });

      const points = buildPoints(coords);
      const bounds = getBounds(atlas, points);
      const map = new atlas.Map(mapDiv, {
        authOptions: {
          authType: atlas.AuthenticationType.subscriptionKey,
          subscriptionKey,
        },
        center: DEFAULT_CENTER,
        zoom: 10,
        maxZoom: 18,
        minZoom: 4,
        style: 'road',
        view: 'Auto',
        language: 'en-US',
      });

      mapRef.current = map;

      map.events.add('ready', async () => {
        if (disposed) return;

        const dataSource = new atlas.source.DataSource(undefined, {
          cluster: true,
          clusterRadius: 45,
          clusterMaxZoom: 16,
        });

        dataSourceRef.current = dataSource;
        map.sources.add(dataSource);

        try {
          await map.imageSprite.add(
            PROVIDER_MARKER_IMAGE_ID,
            iconUrl
          );
        } catch {
          // If the custom marker fails, Azure will still render the layer.
        }

        const clusterLayer = new atlas.layer.SymbolLayer(
          dataSource,
          undefined,
          {
            filter: ['has', 'point_count'],
            iconOptions: {
              image: PROVIDER_MARKER_IMAGE_ID,
              allowOverlap: true,
              ignorePlacement: true,
              anchor: 'center',
              size: 1,
            },
            textOptions: {
              textField: '+',
              color: '#ffffff',
              size: 32,
              font: ['SegoeUi-Bold'],
              offset: [0, 0],
              allowOverlap: true,
              ignorePlacement: true,
            },
          }
        );

        const pointLayer = new atlas.layer.SymbolLayer(
          dataSource,
          undefined,
          {
            filter: ['!', ['has', 'point_count']],
            iconOptions: {
              image: PROVIDER_MARKER_IMAGE_ID,
              allowOverlap: true,
              ignorePlacement: true,
              anchor: 'center',
              size: 1,
            },
            textOptions: {
              textField: ['to-string', ['get', 'index']],
              color: '#ffffff',
              size: 20,
              font: ['SegoeUi-Bold'],
              offset: [0, 0],
              allowOverlap: true,
              ignorePlacement: true,
            },
          }
        );

        map.layers.add([clusterLayer, pointLayer]);

        setMapFeatures(atlas, dataSource, points);

        if (bounds) {
          map.setCamera({
            bounds,
            padding: 60,
          });
        } else {
          map.setCamera({
            center: DEFAULT_CENTER,
            zoom: 10,
          });
        }

        map.events.add('click', pointLayer, (event: any) => {
          const shape = event.shapes?.[0];

          if (!shape || typeof shape.getProperties !== 'function') {
            return;
          }

          const properties = shape.getProperties();

          scrollToLocation(
            Number(properties.originalLatitude),
            Number(properties.originalLongitude)
          );
        });

        map.events.add(
          'click',
          clusterLayer,
          async (event: any) => {
            const shape = event.shapes?.[0];

            if (
              !shape ||
              typeof shape.getProperties !== 'function'
            ) {
              return;
            }

            const properties = shape.getProperties();
            const clusterId = properties?.cluster_id;

            if (
              clusterId == null ||
              !dataSourceRef.current
            ) {
              return;
            }

            const leaves =
              await dataSourceRef.current.getClusterLeaves(
                clusterId,
                Infinity,
                0
              );

            const clusterCoords =
              typeof shape.getCoordinates === 'function'
                ? shape.getCoordinates()
                : shape.geometry?.coordinates;

            if (!clusterCoords) return;

            const expandedPoints = leaves.map(
              (leaf: any, index: number) => {
                const leafProperties =
                  typeof leaf.getProperties === 'function'
                    ? leaf.getProperties()
                    : leaf.properties;

                const angle =
                  (2 * Math.PI * index) / leaves.length;

                const offset = 0.00012;

                return {
                  index: leafProperties.index,
                  latitude:
                    clusterCoords[1] +
                    Math.sin(angle) * offset,
                  longitude:
                    clusterCoords[0] +
                    Math.cos(angle) * offset,
                  originalLatitude:
                    leafProperties.originalLatitude,
                  originalLongitude:
                    leafProperties.originalLongitude,
                };
              }
            );

            setMapFeatures(
              atlas,
              dataSourceRef.current,
              expandedPoints
            );

            map.setCamera({
              center: clusterCoords,
              zoom: Math.min(
                (map.getCamera().zoom ?? 15) + 1,
                18
              ),
              type: 'ease',
              duration: 300,
            });
          }
        );
      });
    };

    initializeAzureMap();

    return () => {
      disposed = true;

      if (mapRef.current) {
        mapRef.current.dispose();
        mapRef.current = null;
      }

      dataSourceRef.current = null;
      atlasRef.current = null;

      if (mapDivRef.current) {
        mapDivRef.current.innerHTML = '';
      }
    };
  }, [coords, iconUrl]);

  return <div />;
};

export default ProvidersLocationsMap;